import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, ClipboardList, CheckCircle, XCircle, Calendar, User, RefreshCw, FileText, Download, Banknote, Send, Clock, Building2, CalendarDays } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Base64 / data URI belgeyi Blob'a çevirip yeni sekmede aç
const openBase64Doc = (doc) => {
  const dataUri = typeof doc === 'string' ? doc : (doc && doc.data);
  if (!dataUri || !dataUri.includes(',')) {
    toast.error('Belge bulunamadı');
    return;
  }
  try {
    const arr = dataUri.split(',');
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    const blob = new Blob([u8], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  } catch (e) {
    toast.error('Belge açılamadı');
  }
};

// Base64 / data URI belgeyi indir
const downloadBase64Doc = (doc) => {
  const dataUri = typeof doc === 'string' ? doc : (doc && doc.data);
  if (!dataUri || !dataUri.includes(',')) {
    toast.error('Belge bulunamadı');
    return;
  }
  try {
    const a = document.createElement('a');
    a.href = dataUri;
    a.download = (doc && doc.name) || 'belge';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    toast.error('Belge indirilemedi');
  }
};

// Avans verisi form_data.form_data altında saklanıyor
const getAvans = (r) => (r.form_data && r.form_data.form_data) ? r.form_data.form_data : (r.form_data || {});

const izinTuruLabel = (t) => ({ yillik: 'Yıllık İzin', mazeret: 'Ücretli İzin', ucretsiz: 'Ücretsiz İzin' }[t] || 'İzin');
const getLeaveDoc = (av) => (Array.isArray(av.izinBelgeleri) && av.izinBelgeleri.length && av.izinBelgeleri[0] && av.izinBelgeleri[0].data) ? av.izinBelgeleri[0] : null;

const formatTL = (v) => {
  const n = parseInt(v);
  return isNaN(n) ? (v || '-') : `${n.toLocaleString('tr-TR')} TL`;
};

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d;
  }
};

const BranchPersonnelRequests = () => {
  const [loading, setLoading] = useState(true);
  const [avansRequests, setAvansRequests] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [avansAccepted, setAvansAccepted] = useState([]);
  const [avansRejected, setAvansRejected] = useState([]);
  const [overtimeAccepted, setOvertimeAccepted] = useState([]);
  const [overtimeRejected, setOvertimeRejected] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveAccepted, setLeaveAccepted] = useState([]);
  const [leaveRejected, setLeaveRejected] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [inboxRes, avAcc, avRej, otAcc, otRej, lvAcc, lvRej] = await Promise.all([
        axios.get(`${API}/branch/hr-requests/inbox?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-accepted?form_template_id=avans&light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-rejected?form_template_id=avans&light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-accepted?form_template_id=overtime&light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-rejected?form_template_id=overtime&light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-accepted?form_template_id=leave&light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/hr-requests/avans-rejected?form_template_id=leave&light=true`, { headers: getAuthHeaders() }),
      ]);
      const inbox = inboxRes.data || [];
      setAvansRequests(inbox.filter((r) => r.form_template_id === 'avans'));
      setOvertimeRequests(inbox.filter((r) => r.form_template_id === 'overtime'));
      setLeaveRequests(inbox.filter((r) => r.form_template_id === 'leave'));
      setAvansAccepted(avAcc.data || []);
      setAvansRejected(avRej.data || []);
      setOvertimeAccepted(otAcc.data || []);
      setOvertimeRejected(otRej.data || []);
      setLeaveAccepted(lvAcc.data || []);
      setLeaveRejected(lvRej.data || []);
      // Sayfa açıldı: bekleyen avans taleplerini görüldü olarak işaretle, sidebar sayacını sıfırla
      try {
        await axios.post(`${API}/branch/personnel-requests/mark-seen`, {}, { headers: getAuthHeaders() });
        window.dispatchEvent(new Event('unread-counts-updated'));
      } catch (_) { /* sessiz geç */ }
    } catch (e) {
      console.error('Talepler yüklenemedi:', e);
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (req) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/branch/hr-requests/${req.id}/action`,
        { action: 'forward_hr', note: 'Şube yönetimi onayladı, İK\'ya iletildi' },
        { headers: getAuthHeaders() }
      );
      toast.success('Talep onaylandı ve İK\'ya iletildi');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Lütfen red sebebini girin');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/branch/hr-requests/${rejectTarget.id}/action`,
        { action: 'reject', note: rejectReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Talep reddedildi');
      setRejectTarget(null);
      setRejectReason('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const renderAvansCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-emerald-500" data-testid={`avans-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-100">
                <User className="h-4 w-4 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
              Şube Onayı Bekliyor
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">Avans Miktarı</p>
              <p className="font-bold text-emerald-700">{formatTL(av.avansMiktari)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Taksit Sayısı</p>
              <p className="font-medium">{av.taksitSayisi || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Form No</p>
              <p className="font-medium">{req.form_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Ödeme Tarihi</p>
              <p className="font-medium">{av.odemeTarihi || '-'}</p>
            </div>
          </div>

          {av.avansSebebi && (
            <div>
              <p className="text-xs text-gray-500">Avans Sebebi</p>
              <p className="text-sm text-gray-700">{av.avansSebebi}</p>
            </div>
          )}

          {av.islakImzaBelgesi && av.islakImzaBelgesi.data && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`view-document-${req.id}`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {av.islakImzaBelgesi.name || 'Belgeyi Görüntüle'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`download-document-${req.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => handleApprove(req)}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`approve-avans-${req.id}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla ve İK'ya İlet
            </Button>
            <Button
              onClick={() => { setRejectTarget(req); setRejectReason(''); }}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`reject-avans-${req.id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Fazla Mesai talep kartı (onay/red ile)
  const renderOvertimeCard = (req) => {
    const ot = getAvans(req);
    const doc = ot.mesaiFormDosya ? { data: ot.mesaiFormDosya, name: ot.mesaiFormDosyaAdi || 'mesai_formu' } : null;
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-amber-500" data-testid={`overtime-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-4 w-4 text-amber-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || ot.mesaiYapanAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)} · Form No: {req.form_number || '-'}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
              Şube Onayı Bekliyor
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">Mesai Tarihi</p>
              <p className="font-medium">{ot.mesaiTarihi || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Mesai Saati</p>
              <p className="font-bold text-amber-700">{ot.mesaiSaati ? `${ot.mesaiSaati} saat` : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Mesai Günü</p>
              <p className="font-medium">{ot.mesaiGunu === 'tatil' ? 'Tatil' : ot.mesaiGunu === 'is_gunu' ? 'İş Günü' : '-'}</p>
            </div>
          </div>

          {ot.mesaiNedeni && (
            <div>
              <p className="text-xs text-gray-500">Mesai Nedeni</p>
              <p className="text-sm text-gray-700">{ot.mesaiNedeni}</p>
            </div>
          )}

          {doc && doc.data && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Islak İmzalı Form</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => openBase64Doc(doc)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`overtime-view-document-${req.id}`}>
                  <FileText className="h-4 w-4 mr-2" />{doc.name}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(doc)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`overtime-download-document-${req.id}`}>
                  <Download className="h-4 w-4 mr-2" />İndir
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => handleApprove(req)}
              disabled={actionLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid={`approve-overtime-${req.id}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla ve İK'ya İlet
            </Button>
            <Button
              onClick={() => { setRejectTarget(req); setRejectReason(''); }}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`reject-overtime-${req.id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // İzin talep kartı (onay/red ile)
  const renderLeaveCard = (req) => {
    const av = getAvans(req);
    const doc = getLeaveDoc(av);
    const isAvansLeave = av.asanTuru === 'avans';
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-sky-500" data-testid={`leave-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-sky-100">
                <CalendarDays className="h-4 w-4 text-sky-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)} · Form No: {req.form_number || '-'}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
              Şube Onayı Bekliyor
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">İzin Türü</p>
              <p className="font-medium">{izinTuruLabel(av.izinTuru)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Başlangıç</p>
              <p className="font-medium">{av.izinBaslangic || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Bitiş</p>
              <p className="font-medium">{av.izinBitis || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sayılan Gün</p>
              <p className="font-bold text-sky-700">{av.hesaplananGun != null ? `${av.hesaplananGun} gün` : '-'}</p>
            </div>
          </div>

          {isAvansLeave && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <CalendarDays className="h-3 w-3 mr-1" /> Avans Yıllık İzin{av.asanGun != null ? ` (${av.asanGun} gün)` : ''}
            </Badge>
          )}

          {av.izinNedeni && (
            <div>
              <p className="text-xs text-gray-500">İzin Nedeni</p>
              <p className="text-sm text-gray-700">{av.izinNedeni}</p>
            </div>
          )}

          {doc && doc.data && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => openBase64Doc(doc)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`leave-view-document-${req.id}`}>
                  <FileText className="h-4 w-4 mr-2" />{doc.name || 'Belgeyi Görüntüle'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(doc)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`leave-download-document-${req.id}`}>
                  <Download className="h-4 w-4 mr-2" />İndir
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => handleApprove(req)}
              disabled={actionLoading}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid={`approve-leave-${req.id}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla ve İK'ya İlet
            </Button>
            <Button
              onClick={() => { setRejectTarget(req); setRejectReason(''); }}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`reject-leave-${req.id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Avans bilgi gridi + belge butonları (salt okunur kartlar için)
  const isOvertime = (req) => req.form_template_id === 'overtime';
  const isLeave = (req) => req.form_template_id === 'leave';

  const getReqDoc = (req) => {
    const d = getAvans(req);
    if (isOvertime(req)) {
      return d.mesaiFormDosya ? { data: d.mesaiFormDosya, name: d.mesaiFormDosyaAdi || 'mesai_formu' } : null;
    }
    if (isLeave(req)) {
      return getLeaveDoc(d);
    }
    return (d.islakImzaBelgesi && d.islakImzaBelgesi.data) ? d.islakImzaBelgesi : null;
  };

  const renderInfoGrid = (req) => {
    const av = getAvans(req);
    if (isOvertime(req)) {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-gray-50 rounded-lg p-3">
          <div>
            <p className="text-xs text-gray-500">Mesai Tarihi</p>
            <p className="font-medium">{av.mesaiTarihi || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Mesai Saati</p>
            <p className="font-bold text-amber-700">{av.mesaiSaati ? `${av.mesaiSaati} saat` : '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Mesai Günü</p>
            <p className="font-medium">{av.mesaiGunu === 'tatil' ? 'Tatil' : av.mesaiGunu === 'is_gunu' ? 'İş Günü' : '-'}</p>
          </div>
          {av.mesaiNedeni && (
            <div className="col-span-2 sm:col-span-3">
              <p className="text-xs text-gray-500">Mesai Nedeni</p>
              <p className="text-sm text-gray-700">{av.mesaiNedeni}</p>
            </div>
          )}
        </div>
      );
    }
    if (isLeave(req)) {
      const isAvansLeave = av.asanTuru === 'avans';
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">İzin Türü</p>
              <p className="font-medium">{izinTuruLabel(av.izinTuru)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Başlangıç</p>
              <p className="font-medium">{av.izinBaslangic || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Bitiş</p>
              <p className="font-medium">{av.izinBitis || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sayılan Gün</p>
              <p className="font-bold text-sky-700">{av.hesaplananGun != null ? `${av.hesaplananGun} gün` : '-'}</p>
            </div>
          </div>
          {isAvansLeave && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <CalendarDays className="h-3 w-3 mr-1" /> Avans Yıllık İzin{av.asanGun != null ? ` (${av.asanGun} gün)` : ''}
            </Badge>
          )}
          {av.izinNedeni && (
            <div>
              <p className="text-xs text-gray-500">İzin Nedeni</p>
              <p className="text-sm text-gray-700">{av.izinNedeni}</p>
            </div>
          )}
        </div>
      );
    }
    return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
        <div>
          <p className="text-xs text-gray-500">Avans Miktarı</p>
          <p className="font-bold text-emerald-700">{formatTL(av.avansMiktari)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Taksit Sayısı</p>
          <p className="font-medium">{av.taksitSayisi || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Ödeme Tarihi</p>
          <p className="font-medium">{av.odemeTarihi || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Avans Sebebi</p>
          <p className="font-medium truncate">{av.avansSebebi || '-'}</p>
        </div>
      </div>
    </>
    );
  };

  const renderDocButtons = (req, prefix) => {
    const doc = getReqDoc(req);
    if (!doc || !doc.data) return null;
    const label = isOvertime(req) ? 'Islak İmzalı Form' : 'Islak İmzalı Belge';
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => openBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-view-document-${req.id}`}>
            <FileText className="h-4 w-4 mr-2" />{doc.name || 'Belgeyi Görüntüle'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-download-document-${req.id}`}>
            <Download className="h-4 w-4 mr-2" />İndir
          </Button>
        </div>
      </div>
    );
  };

  const acceptedBadge = (req) => {
    if (!isOvertime(req) && (req.status === 'avans_given' || req.status === 'completed' || req.accounting_job_status === 'completed')) {
      return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200"><Banknote className="h-3 w-3 mr-1" /> Avans Verildi</Badge>;
    }
    if (req.status === 'pending_accounting') {
      return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200"><Clock className="h-3 w-3 mr-1" /> Muhasebede</Badge>;
    }
    if (req.status === 'pending_hr') {
      return <Badge variant="outline" className="bg-indigo-100 text-indigo-800 border-indigo-200"><Send className="h-3 w-3 mr-1" /> İK'da</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Onaylandı</Badge>;
  };

  const renderAcceptedCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-green-500" data-testid={`accepted-avans-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100">
                <User className="h-4 w-4 text-green-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)} · Form No: {req.form_number || '-'}
                </p>
              </div>
            </div>
            {acceptedBadge(req)}
          </div>
          {renderInfoGrid(req)}
          {renderDocButtons(req, 'accepted')}
        </CardContent>
      </Card>
    );
  };

  const renderRejectedCard = (req) => {
    const av = getAvans(req);
    const rej = req.branch_rejection || req.hr_rejection || req.management_rejection || {};
    const reason = rej.reason || rej.note || req.rejection_reason || '-';
    const byLabel = req.status === 'rejected_by_branch' ? 'Şube' : req.status === 'rejected_by_hr' ? 'İK' : req.status === 'rejected_by_management' ? 'Yönetim' : 'Red';
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-red-400" data-testid={`rejected-avans-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-100">
                <User className="h-4 w-4 text-red-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)} · Form No: {req.form_number || '-'}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
              <XCircle className="h-3 w-3 mr-1" /> {byLabel} Reddetti
            </Badge>
          </div>
          {renderInfoGrid(req)}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium">Red Sebebi</p>
            <p className="text-sm text-red-800 mt-1">{reason}</p>
          </div>
          {renderDocButtons(req, 'rejected')}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="branch-personnel-requests-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 shadow-lg">
            <ClipboardList className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Personel Talepleri</h1>
            <p className="text-sm text-gray-500">Şubenizdeki çalışanların talepleri</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchRequests} data-testid="refresh-personnel-requests">
          <RefreshCw className="h-4 w-4 mr-2" /> Yenile
        </Button>
      </div>

      <Tabs defaultValue="avans" className="w-full">
        <TabsList>
          <TabsTrigger value="avans" data-testid="tab-avans">
            <Wallet className="h-4 w-4 mr-2" />
            Avans Talepleri
            {avansRequests.length > 0 && (
              <Badge className="ml-2 bg-emerald-600 text-white">{avansRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overtime" data-testid="tab-overtime">
            <Clock className="h-4 w-4 mr-2" />
            Fazla Mesai Talepleri
            {overtimeRequests.length > 0 && (
              <Badge className="ml-2 bg-amber-600 text-white">{overtimeRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">
            <CalendarDays className="h-4 w-4 mr-2" />
            İzin Talepleri
            {leaveRequests.length > 0 && (
              <Badge className="ml-2 bg-sky-600 text-white">{leaveRequests.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* AVANS */}
        <TabsContent value="avans" className="mt-4">
          <Tabs defaultValue="avans-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="avans-bekleyen" data-testid="subtab-avans-bekleyen">
                Bekleyen
                {avansRequests.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{avansRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="avans-kabul" data-testid="subtab-avans-kabul">
                Kabul Edilen
                {avansAccepted.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{avansAccepted.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="avans-red" data-testid="subtab-avans-red">
                Red Edilen
                {avansRejected.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{avansRejected.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="avans-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : avansRequests.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Wallet className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Bekleyen avans talebi yok</p></CardContent></Card>
              ) : avansRequests.map(renderAvansCard)}
            </TabsContent>
            <TabsContent value="avans-kabul" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : avansAccepted.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Kabul edilen avans talebi yok</p></CardContent></Card>
              ) : avansAccepted.map(renderAcceptedCard)}
            </TabsContent>
            <TabsContent value="avans-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : avansRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Red edilen avans talebi yok</p></CardContent></Card>
              ) : avansRejected.map(renderRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* FAZLA MESAI */}
        <TabsContent value="overtime" className="mt-4">
          <Tabs defaultValue="overtime-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="overtime-bekleyen" data-testid="subtab-overtime-bekleyen">
                Bekleyen
                {overtimeRequests.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{overtimeRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="overtime-kabul" data-testid="subtab-overtime-kabul">
                Kabul Edilen
                {overtimeAccepted.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{overtimeAccepted.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="overtime-red" data-testid="subtab-overtime-red">
                Red Edilen
                {overtimeRejected.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{overtimeRejected.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overtime-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : overtimeRequests.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Bekleyen fazla mesai talebi yok</p></CardContent></Card>
              ) : overtimeRequests.map(renderOvertimeCard)}
            </TabsContent>
            <TabsContent value="overtime-kabul" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : overtimeAccepted.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Kabul edilen fazla mesai talebi yok</p></CardContent></Card>
              ) : overtimeAccepted.map(renderAcceptedCard)}
            </TabsContent>
            <TabsContent value="overtime-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : overtimeRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Red edilen fazla mesai talebi yok</p></CardContent></Card>
              ) : overtimeRejected.map(renderRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* İZİN */}
        <TabsContent value="leave" className="mt-4">
          <Tabs defaultValue="leave-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="leave-bekleyen" data-testid="subtab-leave-bekleyen">
                Bekleyen
                {leaveRequests.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{leaveRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="leave-kabul" data-testid="subtab-leave-kabul">
                Kabul Edilen
                {leaveAccepted.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{leaveAccepted.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="leave-red" data-testid="subtab-leave-red">
                Red Edilen
                {leaveRejected.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{leaveRejected.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="leave-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : leaveRequests.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CalendarDays className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Bekleyen izin talebi yok</p></CardContent></Card>
              ) : leaveRequests.map(renderLeaveCard)}
            </TabsContent>
            <TabsContent value="leave-kabul" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : leaveAccepted.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Kabul edilen izin talebi yok</p></CardContent></Card>
              ) : leaveAccepted.map(renderAcceptedCard)}
            </TabsContent>
            <TabsContent value="leave-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : leaveRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Red edilen izin talebi yok</p></CardContent></Card>
              ) : leaveRejected.map(renderRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Reddet Modal */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle>Talebi Reddet</DialogTitle>
            <DialogDescription>
              {rejectTarget?.requester_name} adlı çalışanın talebini reddediyorsunuz. Lütfen sebep belirtin.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Red sebebini yazın..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>İptal</Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-btn"
            >
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchPersonnelRequests;
